import { Eye } from '@untitledui/icons';
import { useCallback } from 'react';
import type { Selection } from 'react-aria-components';
import { MenuItem as AriaMenuItem } from 'react-aria-components';

import { ToggleBase } from '@/lib/base/forms/toggle';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { cx } from '@/utils/cx';

import { useEmailTrackingOptional } from './shared';

/** Stable selected-keys for the email settings menu when tracking is on. */
const TRACK_OPENS_SELECTED: Set<string> = new Set(['track-opens']);
/** Stable empty selected-keys when tracking is off. Hoisted to avoid new refs each render. */
const TRACK_OPENS_EMPTY: Set<string> = new Set();

/** Gmail icon badge shown in the ActionAccordion header for email actions */
const GMAIL_BADGE = (
  <div className='inline-flex items-center justify-center px-1.5 py-0.5 border border-border-primary rounded-full'>
    <svg
      className='w-3 h-3'
      viewBox='52 42 88 66'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path fill='#4285f4' d='M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6' />
      <path fill='#34a853' d='M120 108h14c3.32 0 6-2.69 6-6V59l-20 15' />
      <path fill='#fbbc04' d='M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2' />
      <path fill='#ea4335' d='M72 74V48l24 18 24-18v26L96 92' />
      <path
        fill='#c5221f'
        d='M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2'
      />
    </svg>
    <span className='text-[11px] text-fg-tertiary ml-1'>Gmail</span>
  </div>
);

/**
 * Header-right slot for gmail_send actions.
 *
 * Renders the Gmail badge plus a kebab "settings" dropdown that hosts the
 * Track-opens toggle (when the org has email_pixel_tracking enabled and the
 * proposal is editable). Reads tracking state from `EmailTrackingProvider`,
 * which is mounted by both `ProposalDetailPanel` and `InlineProposalExpanded`.
 * Toggling here updates the same context that EmailActionPreview's autosave
 * effect listens to, so flipping it persists to `disable_tracking` on the
 * action payload via the existing autosave path — no new write path needed.
 *
 * Hoisted to module scope (not defined inside MultiActionTimeline) for stable
 * component identity across renders.
 */
export function EmailHeaderRight({ isProcessing }: { isProcessing: boolean }) {
  const tracking = useEmailTrackingOptional();
  const setDisableTracking = tracking?.setDisableTracking;

  // `tracking.trackingEnabled` is initialized by EmailActionPreview's mount
  // effect from the `email_pixel_tracking` feature flag, so it starts false
  // and flips to true once that mount effect runs. Gating on `!isProcessing`
  // mirrors the original footer-toggle behaviour (only interactive when the
  // proposal is editable).
  const showSettingsMenu = !isProcessing && Boolean(tracking?.trackingEnabled);

  // Selection mode: the menu uses `selectionMode='multiple'` so a single
  // checkable item can flip on/off without forcing a one-of-many radio
  // semantics. When the "track-opens" key is in the selected set, tracking
  // is ON (i.e. disableTracking === false).
  const trackingOn = !tracking?.disableTracking;
  const selectedKeys = trackingOn ? TRACK_OPENS_SELECTED : TRACK_OPENS_EMPTY;

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (!setDisableTracking) {
        return;
      }
      // `keys === 'all'` shouldn't happen with a single item but guard anyway.
      const isOn = keys === 'all' || (keys as Set<string>).has('track-opens');
      setDisableTracking(!isOn);
    },
    [setDisableTracking]
  );

  return (
    <div className='inline-flex items-center gap-1'>
      {GMAIL_BADGE}
      {showSettingsMenu && (
        <Dropdown.Root>
          <Dropdown.DotsButton
            aria-label='Email settings'
            className='p-1 hover:bg-[var(--color-bg-tertiary)] rounded-md transition-colors text-fg-quaternary'
          />
          <Dropdown.Popover placement='bottom end'>
            <Dropdown.Menu
              selectionMode='multiple'
              // `Dropdown.Menu` defaults `disallowEmptySelection` to true.
              // We override here so the user can flip Track opens OFF — if
              // it stays true, react-aria silently blocks the only-item
              // deselection and `onSelectionChange` never fires.
              disallowEmptySelection={false}
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
            >
              {/*
                Custom row layout: Eye icon + label on the left, visual Toggle
                on the right. We render `AriaMenuItem` directly here (rather
                than via `Dropdown.Item`) because the wrapper's `unstyled`
                mode drops children, and we need full control over the row to
                place a Toggle where the default item would render a Check
                icon. `ToggleBase` is purely visual (no AriaSwitch wrapper,
                no onChange) so the row's click — wired through the menu's
                `selectionMode='multiple'` mechanics — is the single source
                of truth for state changes; the toggle visual just mirrors
                `state.isSelected`.
              */}
              <AriaMenuItem
                id='track-opens'
                textValue='Track opens'
                className='group block cursor-pointer px-1.5 py-px outline-hidden'
              >
                {state => (
                  <div
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear',
                      state.isFocused && 'bg-primary_hover',
                      state.isFocusVisible && 'outline-2 -outline-offset-2'
                    )}
                  >
                    <Eye
                      aria-hidden='true'
                      className='size-4 shrink-0 stroke-[2.25px] text-fg-quaternary'
                    />
                    <span className='grow text-sm font-semibold text-secondary'>
                      Track opens
                    </span>
                    <ToggleBase
                      size='sm'
                      isSelected={state.isSelected}
                      isHovered={state.isHovered}
                    />
                  </div>
                )}
              </AriaMenuItem>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
    </div>
  );
}
